import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { AgGridReact } from 'ag-grid-react';
import {
  AllCommunityModule,
  ClientSideRowModelModule,
  ModuleRegistry,
} from 'ag-grid-community';
import 'ag-grid-community/styles/ag-theme-quartz.css';
import { getMainAxios } from '../utils/util';
import { useAuth } from '../context/AuthContext';
import { useGridCopyMenu } from './gridCommon';
import IntradayTraceSection from './IntradayTraceSection';

ModuleRegistry.registerModules([AllCommunityModule, ClientSideRowModelModule]);

interface IntradayOrder {
  htsId: string;
  Id: number;
  d: string;
  pfId: number;
  prodId: string;
  prodNm: string;
  ls: number;
  ex: number;
  decision: 'BUY' | 'SELL';
  qty2ord: number;
  cumQty: number;
  avgPx: number | null;
  state: number;
  t0: string;
  genT: string;
  votes: { buy: number; sell: number; hold: number };
  msg: string | null;
}

interface Summary {
  d: string;
  total: number;
  buyCount: number;
  sellCount: number;
  filled: number;
  pending: number;
  fillRate: number;
  cashFlow: number;
}

interface SliceRow {
  Id: number;
  ordId: number;
  d: string;
  t: string;
  qty2ord: number;
  fillCumQty: number;
  limitPrc: number | null;
  ordNo: number | null;
  cnclT: string | null;
  msg?: string | null;
  errMsg?: string | null;
}

const stateLabel = (s: number): string => {
  switch (s) {
    case -10: return '삭제';
    case -9: return '거절';
    case 0: return '미접수';
    case 10: return '접수';
    case 20: return '작동';
    case 30: return '완료';
    default: return String(s);
  }
};

const stateColor = (s: number): string => {
  if (s === 30) return '#22c55e';
  if (s === 20) return '#3b82f6';
  if (s === 10) return '#f59e0b';
  if (s < 0) return '#ef4444';
  return '#6b7280';
};

const NO_SF = {
  sortable: false, filter: false, resizable: true,
  suppressMenu: true, menuTabs: [] as any,
};

const QuantIntradayMonitor: React.FC = () => {
  const copyMenu = useGridCopyMenu();
  const { selectedPfId } = useAuth();
  const axiosInstance = getMainAxios();

  const today = new Date().toISOString().split('T')[0];
  const [d, setD] = useState<string>(today);
  const [orders, setOrders] = useState<IntradayOrder[]>([]);
  const [summary, setSummary] = useState<Summary | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState<IntradayOrder | null>(null);
  const [slices, setSlices] = useState<SliceRow[]>([]);
  const [slicesLoading, setSlicesLoading] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const params: any = { d };
      if (selectedPfId) params.pfId = selectedPfId;
      const [ordRes, sumRes] = await Promise.all([
        axiosInstance.get('/api/v1/quant/intraday/orders', { params }),
        axiosInstance.get('/api/v1/quant/intraday/summary', { params }),
      ]);
      setOrders(ordRes.data || []);
      setSummary(sumRes.data || null);
    } catch (err: any) {
      console.error('intraday 모니터링 조회 실패:', err);
      setError(err?.response?.data?.error || err?.message || '조회 실패');
    } finally {
      setLoading(false);
    }
  }, [d, selectedPfId]);

  const fetchSlices = useCallback(async (ordId: number) => {
    setSlicesLoading(true);
    try {
      const res = await axiosInstance.get('/api/v1/pfbs/slices', { params: { ordId } });
      setSlices(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error('슬라이스 조회 실패:', err);
      setSlices([]);
    } finally {
      setSlicesLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  useEffect(() => {
    if (!autoRefresh) return;
    const h = setInterval(fetchData, 30000);
    return () => clearInterval(h);
  }, [autoRefresh, fetchData]);

  useEffect(() => {
    if (selectedOrder) fetchSlices(selectedOrder.Id);
    else setSlices([]);
  }, [selectedOrder, fetchSlices]);

  useEffect(() => {
    if (selectedOrder) {
      const updated = orders.find(o => o.Id === selectedOrder.Id);
      if (updated && updated.state !== selectedOrder.state) setSelectedOrder(updated);
    }
  }, [orders]);

  const orderColumnDefs = useMemo(() => [
    { headerName: 'HTS', field: 'htsId', width: 80, flex: 0 },
    { headerName: '시각', field: 'genT', width: 90, flex: 0,
      valueFormatter: (p: any) => p.value ? new Date(p.value).toLocaleTimeString('ko-KR', { hour12: false }) : '' },
    { headerName: '종목', field: 'prodId', width: 75, flex: 0 },
    { headerName: '종목명', field: 'prodNm', width: 110, flex: 0 },
    { headerName: '결정', field: 'decision', width: 60, flex: 0,
      cellStyle: (p: any) => ({
        textAlign: 'center', fontWeight: 700,
        color: p.value === 'BUY' ? '#dc2626' : '#2563eb'
      }) },
    { headerName: '투표', width: 90, flex: 0,
      headerTooltip: 'BUY / SELL / HOLD',
      valueGetter: (p: any) => {
        const v = p.data?.votes; if (!v) return '';
        return `${v.buy}/${v.sell}/${v.hold}`;
      },
      cellStyle: { textAlign: 'center', fontFamily: 'monospace' } },
    { headerName: '주문', field: 'qty2ord', width: 65, flex: 0,
      valueFormatter: (p: any) => p.value?.toLocaleString() ?? '',
      cellStyle: { textAlign: 'right' } },
    { headerName: '체결', field: 'cumQty', width: 65, flex: 0,
      valueFormatter: (p: any) => p.value?.toLocaleString() ?? '',
      cellStyle: (p: any) => {
        const r = p.data; if (!r) return { textAlign: 'right' };
        return { textAlign: 'right',
          color: r.cumQty >= r.qty2ord ? '#22c55e' : r.cumQty > 0 ? '#f59e0b' : '#6b7280',
          fontWeight: 600 };
      } },
    { headerName: '체결가', field: 'avgPx', width: 90, flex: 0,
      valueFormatter: (p: any) => p.value ? Math.round(p.value).toLocaleString() : '-',
      cellStyle: { textAlign: 'right' } },
    { headerName: '상태', field: 'state', width: 65, flex: 0,
      valueFormatter: (p: any) => stateLabel(p.value),
      cellStyle: (p: any) => ({ textAlign: 'center', color: stateColor(p.value), fontWeight: 600 }) },
    { headerName: '메모', field: 'msg', width: 380, flex: 0,
      cellStyle: { fontSize: '11px', color: '#6b7280', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
      tooltipField: 'msg' },
  ], []);

  const sliceColumnDefs = useMemo(() => [
    { headerName: '#', width: 45, flex: 0,
      valueGetter: (p: any) => (p.node?.rowIndex ?? 0) + 1,
      cellStyle: { textAlign: 'center', color: '#6b7280' } },
    { headerName: '생성시각', field: 't', width: 140, flex: 0,
      valueFormatter: (p: any) => p.value ? new Date(p.value).toLocaleTimeString('ko-KR', { hour12: false }) : '' },
    { headerName: 'KIS주문번호', field: 'ordNo', width: 110, flex: 0,
      valueFormatter: (p: any) => p.value ?? '-',
      cellStyle: { textAlign: 'right', fontFamily: 'monospace' } },
    { headerName: '주문수량', field: 'qty2ord', width: 80, flex: 0,
      valueFormatter: (p: any) => p.value?.toLocaleString() ?? '',
      cellStyle: { textAlign: 'right' } },
    { headerName: '체결수량', field: 'fillCumQty', width: 80, flex: 0,
      valueFormatter: (p: any) => p.value?.toLocaleString() ?? '',
      cellStyle: (p: any) => {
        const r = p.data; if (!r) return { textAlign: 'right' };
        const full = r.fillCumQty >= r.qty2ord;
        return { textAlign: 'right',
          color: full ? '#22c55e' : r.fillCumQty > 0 ? '#f59e0b' : '#6b7280',
          fontWeight: 600 };
      } },
    { headerName: '지정가', field: 'limitPrc', width: 90, flex: 0,
      valueFormatter: (p: any) => p.value ? Math.round(p.value).toLocaleString() : '-',
      cellStyle: { textAlign: 'right' } },
    { headerName: '취소', field: 'cnclT', width: 110, flex: 0,
      valueFormatter: (p: any) => p.value ? new Date(p.value).toLocaleTimeString('ko-KR', { hour12: false }) : '-',
      cellStyle: (p: any) => ({ textAlign: 'center', color: p.value ? '#ef4444' : '#6b7280' }) },
    { headerName: '메모', field: 'msg', flex: 1, minWidth: 200,
      cellStyle: { fontSize: '11px', color: '#6b7280' } },
    { headerName: '에러', field: 'errMsg', width: 200, flex: 0,
      cellStyle: { fontSize: '11px', color: '#ef4444' } },
  ], []);

  const cardStyle: React.CSSProperties = {
    padding: '10px 14px', backgroundColor: 'white',
    border: '1px solid #d1d5db', borderRadius: '6px',
    minWidth: 110, flex: 1,
  };
  const cardLabel: React.CSSProperties = { fontSize: '11px', color: '#6b7280', marginBottom: '4px' };
  const cardValue: React.CSSProperties = { fontSize: '18px', fontWeight: 700, lineHeight: 1 };
  const hdrStyle: React.CSSProperties = { margin: 0, fontSize: '12px', fontWeight: 600, color: '#1e40af' };
  const panelStyle: React.CSSProperties = {
    backgroundColor: 'white', border: '2px solid #3b82f6', borderRadius: '8px', padding: '14px',
  };

  return (
    <div style={{ padding: '16px', backgroundColor: '#eff6ff', minHeight: '100vh',
                  display: 'flex', flexDirection: 'column', gap: '12px' }}>
      <style>{`
        .ag-theme-quartz .ag-header-cell-menu-button,
        .ag-theme-quartz .ag-header-icon.ag-header-cell-menu-button { display: none !important; }
        .ag-theme-quartz .ag-header-cell-label { padding-right: 2px; }
      `}</style>

      {/* 상단 컨트롤 */}
      <div style={panelStyle}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
          <h3 style={hdrStyle}>Intraday 실시간 모니터링</h3>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center', fontSize: '11px' }}>
            <label>날짜:</label>
            <input type="date" value={d} onChange={e => setD(e.target.value)}
              style={{ padding: '3px 6px', border: '1px solid #d1d5db', borderRadius: '3px', fontSize: '11px' }} />
            <label style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
              <input type="checkbox" checked={autoRefresh} onChange={e => setAutoRefresh(e.target.checked)} />
              30초 자동
            </label>
            <button onClick={fetchData} disabled={loading}
              style={{ padding: '4px 10px', backgroundColor: loading ? '#9ca3af' : '#3b82f6', color: 'white',
                border: 'none', borderRadius: '3px', cursor: loading ? 'not-allowed' : 'pointer', fontSize: '11px' }}>
              {loading ? '조회중...' : '새로고침'}
            </button>
          </div>
        </div>

        {summary && (
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            <div style={cardStyle}>
              <div style={cardLabel}>총 주문</div>
              <div style={cardValue}>{summary.total}</div>
            </div>
            <div style={cardStyle}>
              <div style={cardLabel}>매수 / 매도</div>
              <div style={cardValue}>
                <span style={{ color: '#dc2626' }}>{summary.buyCount}</span>
                <span style={{ color: '#6b7280' }}> / </span>
                <span style={{ color: '#2563eb' }}>{summary.sellCount}</span>
              </div>
            </div>
            <div style={cardStyle}>
              <div style={cardLabel}>체결 완료</div>
              <div style={{ ...cardValue, color: '#22c55e' }}>{summary.filled}</div>
            </div>
            <div style={cardStyle}>
              <div style={cardLabel}>미체결</div>
              <div style={{ ...cardValue, color: summary.pending > 0 ? '#f59e0b' : '#6b7280' }}>{summary.pending}</div>
            </div>
            <div style={cardStyle}>
              <div style={cardLabel}>체결률</div>
              <div style={cardValue}>{summary.fillRate}%</div>
            </div>
            <div style={cardStyle}>
              <div style={cardLabel}>당일 현금흐름</div>
              <div style={{ ...cardValue,
                color: summary.cashFlow > 0 ? '#22c55e' : summary.cashFlow < 0 ? '#ef4444' : '#6b7280' }}>
                {summary.cashFlow.toLocaleString()}
              </div>
            </div>
          </div>
        )}

        {error && <div style={{ color: '#ef4444', fontSize: '11px', marginTop: '8px' }}>{error}</div>}
      </div>

      {/* 주문 내역 */}
      <div style={panelStyle}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
          <h3 style={hdrStyle}>주문 내역 ({orders.length}건)</h3>
          <span style={{ fontSize: '10px', color: '#6b7280' }}>행 클릭 → 하단 상세</span>
        </div>
        <div className="ag-theme-quartz" style={{ height: 280 }}>
          <AgGridReact
            rowData={orders}
            columnDefs={orderColumnDefs as any}
            defaultColDef={NO_SF}
            rowHeight={26}
            headerHeight={30}
            rowSelection="single"
            onRowClicked={(e) => setSelectedOrder(e.data)}
            getRowStyle={(p: any) => selectedOrder && p.data?.Id === selectedOrder.Id
              ? { backgroundColor: '#dbeafe' } : undefined}
            getContextMenuItems={copyMenu as any}
            animateRows={false}
          />
        </div>
      </div>

      {/* 주문 상세 */}
      <div style={panelStyle}>
        <h3 style={{ ...hdrStyle, marginBottom: '8px' }}>
          주문 상세 {selectedOrder ? `- #${selectedOrder.Id} ${selectedOrder.prodId} ${selectedOrder.prodNm}` : ''}
        </h3>

        {!selectedOrder && (
          <div style={{ padding: '30px', textAlign: 'center', color: '#6b7280', fontSize: '12px' }}>
            상단 그리드에서 주문을 선택하세요
          </div>
        )}

        {selectedOrder && (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
                          gap: '8px', marginBottom: '12px', fontSize: '11px' }}>
              <div><b>계좌:</b> {selectedOrder.htsId} (pfId={selectedOrder.pfId})</div>
              <div><b>결정:</b> <span style={{ color: selectedOrder.decision === 'BUY' ? '#dc2626' : '#2563eb',
                fontWeight: 700 }}>{selectedOrder.decision}</span></div>
              <div><b>상태:</b> <span style={{ color: stateColor(selectedOrder.state), fontWeight: 600 }}>
                {stateLabel(selectedOrder.state)}</span></div>
              <div><b>주문수량:</b> {selectedOrder.qty2ord.toLocaleString()}</div>
              <div><b>체결수량:</b> <span style={{
                color: selectedOrder.cumQty >= selectedOrder.qty2ord ? '#22c55e' : '#f59e0b', fontWeight: 600 }}>
                {selectedOrder.cumQty.toLocaleString()}</span></div>
              <div><b>체결가:</b> {selectedOrder.avgPx ? Math.round(selectedOrder.avgPx).toLocaleString() : '-'}</div>
              <div><b>전략 투표(B/S/H):</b> <span style={{ fontFamily: 'monospace' }}>
                {selectedOrder.votes.buy}/{selectedOrder.votes.sell}/{selectedOrder.votes.hold}</span></div>
              <div><b>생성시각:</b> {new Date(selectedOrder.genT).toLocaleTimeString('ko-KR', { hour12: false })}</div>
              <div><b>시작예정(t0):</b> {new Date(selectedOrder.t0).toLocaleTimeString('ko-KR', { hour12: false })}</div>
              <div style={{ gridColumn: '1 / -1', color: '#6b7280' }}><b>메모:</b> {selectedOrder.msg || '-'}</div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
              <h4 style={{ margin: 0, fontSize: '11px', fontWeight: 600, color: '#1e40af' }}>
                슬라이스 {slicesLoading ? '(조회중...)' : `(${slices.length}건)`}
              </h4>
            </div>
            <div className="ag-theme-quartz" style={{ height: 220 }}>
              <AgGridReact
                rowData={slices}
                columnDefs={sliceColumnDefs as any}
                defaultColDef={NO_SF}
                rowHeight={24}
                headerHeight={28}
                getContextMenuItems={copyMenu as any}
                animateRows={false}
              />
            </div>
          </>
        )}
      </div>

      {/* 실행 트레이스 (공통 컴포넌트) */}
      <IntradayTraceSection initial={{ prodId: '005930' }} />
    </div>
  );
};

export default QuantIntradayMonitor;
